import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  InvoiceMailAttachmentPreview,
  isPdfAttachment,
} from "@/components/invoiceMail/InvoiceMailAttachmentPreview";
import { ApiError } from "@/lib/apiClient";
import { formatEuro } from "@/lib/formatMoney";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";
import {
  confirmInvoiceMailCandidate,
  confidenceBadgeClass,
  confidenceLabel,
  einvoiceLineCountFromCandidate,
  getInvoiceMailEinvoice,
  invoiceMailAttachmentUrl,
  searchInvoiceMailBestellnummern,
  stammMatchLabel,
  updateInvoiceMailMatches,
  type EingangsrechnungArtikelLine,
  type InvoiceMailBestellnummerSearchItem,
  type InvoiceMailCandidate,
  type InvoiceMailEinvoiceResponse,
} from "@/lib/invoiceMail";

type SelectedTarget = {
  auftrag_id: number;
  bestellnummer: string;
  label: string;
  matchKind: "bestellung" | "projekt";
  displayNumber: string;
};

function targetKey(row: Pick<SelectedTarget, "auftrag_id" | "matchKind" | "displayNumber">): string {
  return `${row.auftrag_id}|${row.matchKind}|${row.displayNumber}`;
}

function isProjectMatch(t: {
  match_kind?: string | null;
  bestellnummer?: string | null;
  auftragsnummer?: string | null;
}): boolean {
  if (t.match_kind === "projekt") return true;
  if (t.match_kind === "bestellung") return false;
  return !(t.bestellnummer || "").trim() && !!(t.auftragsnummer || "").trim();
}

function projectDetails(t: {
  jahr?: number | null;
  kunde?: string | null;
  auftrag_beschreibung?: string | null;
}): string {
  return [t.jahr ? String(t.jahr) : "", (t.kunde || "").trim(), (t.auftrag_beschreibung || "").trim()]
    .filter(Boolean)
    .join(" – ");
}

function projectLabel(t: {
  auftragsnummer?: string | null;
  auftrag_id?: number | null;
  jahr?: number | null;
  kunde?: string | null;
  auftrag_beschreibung?: string | null;
}): string {
  const details = projectDetails(t);
  return `Projekt ${t.auftragsnummer || t.auftrag_id}${details ? ` – ${details}` : ""}`;
}

function fmtAttachmentSize(bytes?: number): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  return `${Math.round(n / 1024)} KB`;
}

function fmtQty(value?: number | null): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function senderLabel(row: InvoiceMailCandidate): string {
  const name = (row.from_name || "").trim();
  const email = (row.from_email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return name || email || "Unbekannter Absender";
}

function toSelectedTarget(
  t: InvoiceMailBestellnummerSearchItem | {
    auftrag_id?: number | null;
    bestellnummer?: string | null;
    auftragsnummer?: string | null;
    sharepoint_folder_id?: string | null;
    match_kind?: "bestellung" | "projekt" | null;
    jahr?: number | null;
    kunde?: string | null;
    auftrag_beschreibung?: string | null;
  },
): SelectedTarget | null {
  if (t.auftrag_id == null || !t.sharepoint_folder_id) return null;
  const bestellnummer = (t.bestellnummer || "").trim();
  const project = isProjectMatch(t);
  if (!bestellnummer && !project) return null;
  if (project && !(t.auftragsnummer || "").trim()) return null;
  return {
    auftrag_id: t.auftrag_id,
    bestellnummer,
    matchKind: project ? "projekt" : "bestellung",
    displayNumber: project ? (t.auftragsnummer || "").trim() : bestellnummer,
    label: project
      ? projectLabel(t)
      : `Auftrag ${t.auftragsnummer || t.auftrag_id} – ${bestellnummer}`,
  };
}

function initialSelectedTargets(candidate: InvoiceMailCandidate): SelectedTarget[] {
  const targets = candidate.match_targets || [];
  const manual = (candidate.match_reasons || []).includes("manual");
  const orders = targets.filter((target) => !isProjectMatch(target));
  const projects = targets.filter((target) => isProjectMatch(target));
  const chosen = [...orders];
  if (manual || (orders.length === 0 && projects.length === 1)) {
    chosen.push(...projects);
  }
  const out: SelectedTarget[] = [];
  const seen = new Set<string>();
  for (const target of chosen) {
    const row = toSelectedTarget(target);
    if (!row) continue;
    const key = targetKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function statusBadgeClass(status: InvoiceMailCandidate["status"]): string {
  switch (status) {
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "confirmed":
      return "bg-status-green/15 text-status-green";
    case "dismissed":
      return "bg-label-secondary/15 text-label-secondary";
    default:
      return "bg-label-secondary/10 text-label-secondary";
  }
}

function statusLabel(status: InvoiceMailCandidate["status"]): string {
  switch (status) {
    case "pending":
      return "Offen";
    case "confirmed":
      return "Abgelegt";
    case "dismissed":
      return "Verworfen";
    case "skipped":
      return "Ohne Anhang";
    default:
      return status;
  }
}

function initAttachmentTks(candidate: InvoiceMailCandidate): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const att of candidate.attachments_summary || []) {
    const id = att.id ? String(att.id) : "";
    if (!id) continue;
    out[id] = [...(att.matched_tks || [])];
  }
  return out;
}

export function ConfirmInvoiceMailDialog({
  open,
  candidate,
  defaultSubfolder,
  onClose,
  onConfirmed,
  onUpdated,
}: {
  open: boolean;
  candidate: InvoiceMailCandidate | null;
  defaultSubfolder: string;
  onClose: () => void;
  onConfirmed: () => void;
  onUpdated?: (candidate: InvoiceMailCandidate) => void;
}) {
  const [localCandidate, setLocalCandidate] = useState<InvoiceMailCandidate | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<SelectedTarget[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(new Set());
  const [attachmentTks, setAttachmentTks] = useState<Record<string, string[]>>({});
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [newTkInput, setNewTkInput] = useState("");
  const [includeEml, setIncludeEml] = useState(true);
  const [moveInbox, setMoveInbox] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savingMatches, setSavingMatches] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);

  const [searchQ, setSearchQ] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<InvoiceMailBestellnummerSearchItem[]>([]);

  const [einvoiceLoading, setEinvoiceLoading] = useState(false);
  const [einvoice, setEinvoice] = useState<InvoiceMailEinvoiceResponse | null>(null);

  const activeCandidate =
    open && candidate
      ? localCandidate && localCandidate.id === candidate.id
        ? localCandidate
        : candidate
      : null;

  const attachments = useMemo(
    () => (activeCandidate?.attachments_summary || []).filter((a) => a.id),
    [activeCandidate],
  );

  const resolvedPreviewId = (() => {
    if (previewId && attachments.some((attachment) => String(attachment.id) === previewId)) {
      return previewId;
    }
    const fallback = attachments.find((attachment) => isPdfAttachment(attachment)) || attachments[0];
    return fallback?.id ? String(fallback.id) : null;
  })();

  const previewAtt = attachments.find((a) => String(a.id) === resolvedPreviewId) || null;
  const einvoiceLines = activeCandidate ? einvoiceLineCountFromCandidate(activeCandidate) : 0;
  const isPending = activeCandidate?.status === "pending";

  useEffect(() => {
    if (!open || !candidate) return;
    setLocalCandidate(candidate);
    setIncludeEml(true);
    setMoveInbox(true);
    setError(null);
    setSaveOk(null);
    setSearchQ("");
    setSearchResults([]);
    setNewTkInput("");

    setSelectedTargets(initialSelectedTargets(candidate));
    setAttachmentTks(initAttachmentTks(candidate));

    const summaryIds = (candidate.attachments_summary || [])
      .filter((a) => a?.id != null)
      .map((a) => String(a.id));
    setSelectedAttachments(new Set(summaryIds));

    const defaultPreview =
      (candidate.attachments_summary || []).find((a) => isPdfAttachment(a))?.id ||
      candidate.attachments_summary?.[0]?.id ||
      null;
    setPreviewId(defaultPreview ? String(defaultPreview) : null);

    setEinvoice(null);
    setEinvoiceLoading(true);
    let cancelled = false;
    void getInvoiceMailEinvoice(candidate.id)
      .then((data) => {
        if (!cancelled) setEinvoice(data);
      })
      .catch(() => {
        if (!cancelled) setEinvoice(null);
      })
      .finally(() => {
        if (!cancelled) setEinvoiceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, candidate]);

  useEffect(() => {
    if (!open) return;
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setSearchLoading(true);
      void searchInvoiceMailBestellnummern(q)
        .then(setSearchResults)
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [open, searchQ]);

  function removeTarget(row: SelectedTarget) {
    const key = targetKey(row);
    setSelectedTargets((prev) => prev.filter((item) => targetKey(item) !== key));
  }

  function addSearchResult(item: InvoiceMailBestellnummerSearchItem) {
    const row = toSelectedTarget(item);
    if (!row) return;
    const key = targetKey(row);
    setSelectedTargets((prev) => {
      if (prev.some((item) => targetKey(item) === key)) return prev;
      return [...prev, row];
    });
    setSearchQ("");
    setSearchResults([]);
  }

  function toggleAttachment(id: string) {
    setSelectedAttachments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addTkToPreviewAttachment(tk: string) {
    const attachmentId = resolvedPreviewId;
    if (!attachmentId) return;
    const cleaned = tk.trim();
    if (!cleaned) return;
    setAttachmentTks((prev) => {
      const current = prev[attachmentId] || [];
      if (current.includes(cleaned)) return prev;
      return { ...prev, [attachmentId]: [...current, cleaned] };
    });
    setNewTkInput("");
  }

  function removeTkFromPreviewAttachment(tk: string) {
    const attachmentId = resolvedPreviewId;
    if (!attachmentId) return;
    setAttachmentTks((prev) => ({
      ...prev,
      [attachmentId]: (prev[attachmentId] || []).filter((x) => x !== tk),
    }));
  }

  async function handleSaveMatches() {
    if (!activeCandidate) return;
    if (selectedTargets.length === 0) {
      setError("Bitte mindestens eine Bestellnummer / ein Ziel hinterlegen.");
      return;
    }
    setSavingMatches(true);
    setError(null);
    setSaveOk(null);
    try {
      const res = await updateInvoiceMailMatches(activeCandidate.id, {
        targets: selectedTargets.map((t) => ({
          auftrag_id: t.auftrag_id,
          bestellnummer: t.bestellnummer || null,
          subfolder: defaultSubfolder,
        })),
        attachment_tks: attachmentTks,
      });
      setLocalCandidate(res.candidate);
      onUpdated?.(res.candidate);
      setSaveOk("Zuordnung gespeichert.");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Speichern fehlgeschlagen.";
      setError(msg);
    } finally {
      setSavingMatches(false);
    }
  }

  async function handleConfirm() {
    if (!activeCandidate) return;
    if (selectedTargets.length === 0) {
      setError("Bitte mindestens einen Zielordner auswählen oder korrigieren.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await confirmInvoiceMailCandidate(activeCandidate.id, {
        sharepoint_targets: selectedTargets.map((t) => ({
          auftrag_id: t.auftrag_id,
          bestellnummer: t.bestellnummer || null,
          subfolder: defaultSubfolder,
        })),
        include_eml: includeEml,
        selected_attachment_ids: selectedAttachments.size > 0 ? [...selectedAttachments] : [],
        move_in_mailbox_to: moveInbox ? "Verarbeitet" : null,
      });
      onConfirmed();
      onClose();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Freigabe fehlgeschlagen.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (!activeCandidate) return null;

  const previewTks = resolvedPreviewId ? attachmentTks[resolvedPreviewId] || [] : [];
  const selectedAuftragIds = new Set(selectedTargets.map((target) => target.auftrag_id));
  const projectSuggestions = (activeCandidate.match_targets || []).filter(
    (target) =>
      isProjectMatch(target) &&
      target.auftrag_id != null &&
      !selectedAuftragIds.has(target.auftrag_id),
  );
  const recognizedProjects = (activeCandidate.matched_bestellnummern || []).filter((value) =>
    /^\d+(?:-\d+)+#\d+/.test(value),
  );
  const unmatchedProjectNumbers = recognizedProjects.filter(
    (value) =>
      !(activeCandidate.match_targets || []).some((target) => {
        const needle = value.toUpperCase();
        return [target.matched_fragment, target.auftragsnummer].some(
          (entry) => (entry || "").toUpperCase() === needle,
        );
      }),
  );

  return (
    <Dialog open={open} onClose={onClose} title={activeCandidate.subject || "Rechnung prüfen"} size="4xl">
      <div className="space-y-4 text-sm">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-separator bg-fill-quaternary/10 px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs text-label-secondary">{senderLabel(activeCandidate)}</div>
            <div className="text-xs text-label-secondary">
              {activeCandidate.received_at ? fmtDateTime(activeCandidate.received_at) : "—"}
            </div>
            {activeCandidate.snippet ? (
              <p className="mt-2 line-clamp-2 text-label-secondary">{activeCandidate.snippet}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                activeCandidate.has_attachments
                  ? "bg-accent/15 text-accent"
                  : "bg-label-secondary/15 text-label-secondary"
              }`}
            >
              {activeCandidate.has_attachments ? "Mit Anhang" : "Ohne Anhang"}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(activeCandidate.status)}`}
            >
              {statusLabel(activeCandidate.status)}
            </span>
            {einvoiceLines > 0 ? (
              <Link
                to={`/bestellwesen/eingangsrechnung-artikel?candidate_id=${activeCandidate.id}`}
                className="rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green hover:underline"
              >
                E-Rechnung: {einvoiceLines} Position{einvoiceLines === 1 ? "" : "en"}
              </Link>
            ) : (
              <span className="rounded-full bg-label-secondary/10 px-2 py-0.5 text-xs font-semibold text-label-secondary">
                Keine E-Rechnung erkannt
              </span>
            )}
            {selectedTargets.length > 0 ? (
              <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-semibold text-accent">
                {selectedTargets.length} Ziel{selectedTargets.length === 1 ? "" : "e"}
              </span>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <section className="space-y-3">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-label-secondary">
                Anhänge ({attachments.length})
              </div>
              {attachments.length === 0 ? (
                <p className="text-label-secondary">Keine Anhänge vorhanden.</p>
              ) : (
                <>
                  <ul className="flex flex-wrap gap-2">
                    {attachments.map((a) => {
                      const id = String(a.id);
                      const active = id === resolvedPreviewId;
                      const attTks = attachmentTks[id] || [];
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            onClick={() => setPreviewId(id)}
                            className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                              active
                                ? "border-accent bg-accent/10 text-label-primary"
                                : "border-separator bg-card hover:border-accent/40"
                            }`}
                          >
                            <div className="font-medium">{a.name || id}</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {a.einvoice_parsed ? (
                                <span className="rounded bg-status-green/15 px-1 py-0.5 text-status-green">
                                  E-Rechnung
                                </span>
                              ) : null}
                              {attTks.length ? (
                                attTks.map((tk) => (
                                  <span
                                    key={tk}
                                    className="rounded bg-accent/10 px-1 py-0.5 font-mono text-accent"
                                  >
                                    {tk}
                                  </span>
                                ))
                              ) : (
                                <span className="text-label-secondary">(keine Nummer)</span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {previewAtt ? (
                    <div className="mt-3 space-y-3">
                      <InvoiceMailAttachmentPreview
                        key={`${activeCandidate.id}:${previewAtt.id}`}
                        candidateId={activeCandidate.id}
                        attachment={previewAtt}
                      />
                      {isPending ? (
                        <div className="rounded-lg border border-separator bg-card/50 p-3">
                          <div className="mb-2 text-xs font-medium text-label-secondary">
                            Nummern für „{previewAtt.name || previewAtt.id}“
                          </div>
                          <div className="mb-2 flex flex-wrap gap-1">
                            {previewTks.length === 0 ? (
                              <span className="text-xs text-label-secondary">Noch keine Nummer</span>
                            ) : (
                              previewTks.map((tk) => (
                                <span
                                  key={tk}
                                  className="inline-flex items-center gap-1 rounded bg-accent/10 px-2 py-0.5 font-mono text-xs text-accent"
                                >
                                  {tk}
                                  <button
                                    type="button"
                                    className="text-label-secondary hover:text-status-red"
                                    onClick={() => removeTkFromPreviewAttachment(tk)}
                                    aria-label={`${tk} entfernen`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))
                            )}
                          </div>
                          <div className="flex gap-2">
                            <input
                              className="min-w-0 flex-1 rounded border border-separator bg-card px-2 py-1.5 font-mono text-xs"
                              placeholder="Nummer, z. B. TK26-1-1#123-706-GMK oder 1-2#546"
                              value={newTkInput}
                              onChange={(e) => setNewTkInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addTkToPreviewAttachment(newTkInput);
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => addTkToPreviewAttachment(newTkInput)}
                            >
                              Hinzufügen
                            </Button>
                          </div>
                        </div>
                      ) : null}

                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {isPending ? (
                          <label className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              checked={selectedAttachments.has(String(previewAtt.id))}
                              onChange={() => toggleAttachment(String(previewAtt.id))}
                            />
                            Beim Ablegen mitnehmen
                          </label>
                        ) : null}
                        <a
                          href={invoiceMailAttachmentUrl(activeCandidate.id, String(previewAtt.id))}
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent hover:underline"
                        >
                          In neuem Tab öffnen
                        </a>
                        {fmtAttachmentSize(previewAtt.size ?? undefined) ? (
                          <span className="text-label-secondary">
                            {fmtAttachmentSize(previewAtt.size ?? undefined)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <div className="rounded-xl border border-separator p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="font-medium">Bestellnummern / Ablageziele</div>
                {isPending ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={savingMatches || selectedTargets.length === 0}
                    onClick={() => void handleSaveMatches()}
                  >
                    {savingMatches ? <Spinner className="h-3.5 w-3.5" /> : "Zuordnung speichern"}
                  </Button>
                ) : null}
              </div>

              {selectedTargets.length === 0 ? (
                <p className="text-label-secondary">
                  Keine Zuordnung – wähle ein vorgeschlagenes Projekt oder suche eine Nummer.
                </p>
              ) : (
                <ul className="space-y-2">
                  {selectedTargets.map((t) => {
                    const meta = (activeCandidate.match_targets || []).find((m) => {
                      if (m.auftrag_id !== t.auftrag_id) return false;
                      if (t.matchKind === "projekt") return isProjectMatch(m);
                      return (m.bestellnummer || "").toUpperCase() === t.bestellnummer.toUpperCase();
                    });
                    return (
                      <li
                        key={targetKey(t)}
                        className="rounded border border-separator/70 bg-card/40 px-3 py-2 text-xs"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            {meta?.match_confidence ? (
                              <span
                                className={`mr-2 rounded px-1.5 py-0.5 font-medium ${confidenceBadgeClass(meta.match_confidence)}`}
                              >
                                {confidenceLabel(meta.match_confidence)}
                              </span>
                            ) : (
                              <span className="mr-2 rounded bg-accent/10 px-1.5 py-0.5 font-medium text-accent">
                                Manuell
                              </span>
                            )}
                            <span className="font-mono text-accent">{t.displayNumber}</span>
                            <div className="mt-1 text-label-primary">{t.label}</div>
                            <div className="font-mono text-label-secondary">{defaultSubfolder}/</div>
                          </div>
                          {isPending ? (
                            <button
                              type="button"
                              className="text-label-secondary hover:text-status-red"
                              onClick={() => removeTarget(t)}
                              aria-label="Ziel entfernen"
                            >
                              ×
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {projectSuggestions.length > 0 || unmatchedProjectNumbers.length > 0 ? (
                <div className="mt-3 space-y-2">
                  <div className="text-xs font-medium text-label-secondary">Mögliche Projekte</div>
                  {unmatchedProjectNumbers.length > 0 ? (
                    <p className="text-xs text-status-orange">
                      Projektnummer {unmatchedProjectNumbers.join(", ")} erkannt, kein passendes Projekt gefunden.
                    </p>
                  ) : null}
                  {projectSuggestions.length > 0 ? (
                    <ul className="max-h-40 space-y-1 overflow-y-auto rounded border border-separator p-2">
                      {projectSuggestions.map((item) => {
                        const canFile = Boolean(item.sharepoint_folder_id);
                        return (
                          <li key={`${item.auftrag_id}-${item.jahr || ""}`}>
                            <button
                              type="button"
                              disabled={!isPending || !canFile}
                              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-accent/10 disabled:cursor-default disabled:hover:bg-transparent"
                              onClick={() => addSearchResult(item)}
                            >
                              <span className="font-mono text-accent">
                                {item.auftragsnummer || item.matched_fragment}
                              </span>
                              <span className="ml-2 text-label-secondary">
                                {projectDetails(item) || "Projekt"}
                              </span>
                              {!canFile ? (
                                <span className="ml-2 text-status-orange">ohne SharePoint-Ordner</span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {isPending ? (
                <div className="mt-3 space-y-2">
                  <input
                    className="w-full rounded border border-separator bg-card px-3 py-2 text-sm"
                    placeholder="Bestellnummer oder Projektnummer suchen…"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                  />
                  {searchLoading ? (
                    <div className="text-xs text-label-secondary">Suche…</div>
                  ) : null}
                  {searchResults.length > 0 ? (
                    <ul className="max-h-36 space-y-1 overflow-y-auto rounded border border-separator p-2">
                      {searchResults.map((item) => {
                        const project = isProjectMatch(item);
                        return (
                          <li key={`${item.auftrag_id}-${item.bestellnummer || item.auftragsnummer}-${item.jahr || ""}`}>
                            <button
                              type="button"
                              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-accent/10"
                              onClick={() => addSearchResult(item)}
                            >
                              <span className="font-mono text-accent">
                                {project ? item.auftragsnummer : item.bestellnummer}
                              </span>
                              <span className="ml-2 text-label-secondary">
                                {project
                                  ? projectDetails(item) || "Projekt"
                                  : `Auftrag ${item.auftragsnummer || item.auftrag_id}${
                                      item.auftrag_beschreibung ? ` – ${item.auftrag_beschreibung}` : ""
                                    }`}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>

            {einvoiceLoading ? (
              <div className="flex items-center gap-2 text-sm text-label-secondary">
                <Spinner className="h-4 w-4" />
                E-Rechnungspositionen werden geladen…
              </div>
            ) : null}

            {!einvoiceLoading && (einvoice?.lines?.length || 0) > 0 ? (
              <div className="rounded-xl border border-separator p-3">
                <div className="mb-2 font-medium">
                  E-Rechnung ({einvoice?.lines.length} Position
                  {einvoice?.lines.length === 1 ? "" : "en"})
                </div>
                <div className="max-h-48 overflow-auto rounded border border-separator">
                  <table className="w-full min-w-[480px] text-left text-xs">
                    <thead className="sticky top-0 bg-card text-label-secondary">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">Bezeichnung</th>
                        <th className="px-2 py-1.5 font-medium text-right">Menge</th>
                        <th className="px-2 py-1.5 font-medium text-right">Netto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(einvoice?.lines || []).slice(0, 12).map((line: EingangsrechnungArtikelLine) => (
                        <tr key={line.id} className="border-t border-separator/60">
                          <td className="px-2 py-1.5 align-top">
                            <div className="font-medium">{line.bezeichnung}</div>
                            {line.stamm_artikel_id ? (
                              <div className="text-status-green">{stammMatchLabel(line)}</div>
                            ) : (
                              <div className="text-label-secondary">Kein Stamm-Match</div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right align-top whitespace-nowrap">
                            {fmtQty(line.menge)}
                          </td>
                          <td className="px-2 py-1.5 text-right align-top tabular-nums">
                            {formatEuro(line.zeilen_netto, 2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(einvoice?.lines.length || 0) > 12 ? (
                  <p className="mt-2 text-xs text-label-secondary">
                    … und {(einvoice?.lines.length || 0) - 12} weitere Positionen.{" "}
                    <Link
                      to={`/bestellwesen/eingangsrechnung-artikel?candidate_id=${activeCandidate.id}`}
                      className="text-accent hover:underline"
                    >
                      Alle anzeigen
                    </Link>
                  </p>
                ) : null}
              </div>
            ) : null}

            {isPending ? (
              <div className="space-y-2 rounded-xl border border-separator p-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeEml}
                    onChange={(e) => setIncludeEml(e.target.checked)}
                  />
                  Mail als .eml mit ablegen
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={moveInbox}
                    onChange={(e) => setMoveInbox(e.target.checked)}
                  />
                  Mail nach Freigabe in Ordner „Verarbeitet“ verschieben
                </label>
              </div>
            ) : null}
          </section>
        </div>

        {saveOk ? <p className="text-sm text-status-green">{saveOk}</p> : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-3">
          <Button variant="secondary" onClick={onClose} disabled={loading || savingMatches}>
            Schließen
          </Button>
          {isPending ? (
            <Button
              onClick={() => void handleConfirm()}
              disabled={loading || savingMatches || selectedTargets.length === 0}
            >
              {loading ? <Spinner className="h-3.5 w-3.5" /> : "Freigeben und ablegen"}
            </Button>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
